import classNames from "classnames"
import { useApp } from "../../../hooks/useApp"
import { getActiveMaterialByMainProductVariantId, getFirstAvailablePendantVariantIdByMetalAndPendantProductId } from "../../../utils" 
import { DragEventHandler, useEffect, useState } from "react"

const MIN_NUMBER_OF_PENDANTS = 2

interface Props {
  className?: string
}

export const ActivePendants = ({ className }: Props) => {
  const { 
    active, 
    setActive, 
    mainProduct, 
    activePendantProductVaraintIds, 
    setActivePendantProductVaraintIds,
    activePendantProductId,
    activeMainProductVariantId,
  } = useApp()

  const { dragAndDropEnabled } = mainProduct.globalSettings
  const [movingItemIndex, setMovingItemIndex] = useState<null | number>(null)
  const [hoveredItemIndex, setHoveredItemIndex] = useState<null | number>(null)
  const [hoveredSide, setHoveredSide] = useState<null | 'left' | 'right'>(null)
  const [iosShakeOn, setIosShakeOn] = useState(true)

  useEffect(() => {
    setTimeout(() => setIosShakeOn(false), 3000)
  }, [])

  const handlePendantAdd = () => {
    const nextActivePendantProductVaraintIds = [
      ...activePendantProductVaraintIds, 
      getFirstAvailablePendantVariantIdByMetalAndPendantProductId(
        mainProduct, 
        getActiveMaterialByMainProductVariantId(mainProduct, activeMainProductVariantId), activePendantProductId
      )
    ]
    setActivePendantProductVaraintIds(nextActivePendantProductVaraintIds)

    setActive(activePendantProductVaraintIds.length)
  }

  const handlePendantRemove = (index: number) => {
    const nextActivePendantProductVaraintIds = [
      ...activePendantProductVaraintIds.slice(0, index), 
      ...activePendantProductVaraintIds.slice(
        index + 1, 
        activePendantProductVaraintIds.length
      )
    ]
    setActivePendantProductVaraintIds(nextActivePendantProductVaraintIds)

    const nextActive = index === activePendantProductVaraintIds.length - 1
      ? index - 1
      : index

    setActive(nextActive)
  }

  const variants = mainProduct.metafields.pendantProducts
    .flatMap(pendantProduct => pendantProduct.variants)

  const MAX_NUMBER_OF_PENDANTS = mainProduct.metafields.type === 'Necklace' ? 6 : 5

  return (
    <ul className={classNames(
      "flex gap-3 select-none", 
      {
        'flex-wrap': movingItemIndex === null
      },
      className
    )}>
      {activePendantProductVaraintIds.map((activePendantProductVaraintId, index) => {
        // @TODO: Move to utilities
        const pendantProductVariant = variants.find((variant) => Number(variant.id) === Number(activePendantProductVaraintId))

        const handlePendantSelect = () => {
          setActive(index)
        }

        const handleDrugStart: DragEventHandler<HTMLLIElement> = (e) => {
          // Hack to make it work on mobile devices
          e.dataTransfer.setData('text/plain', String(index))
          
          setMovingItemIndex(index)
        } 

        const handleDragOver: DragEventHandler<HTMLLIElement> = (e) => {
          e.preventDefault()
          e.dataTransfer.dropEffect = 'move'
          if (index === movingItemIndex) return 
          setHoveredItemIndex(index)

          const rect = e.currentTarget.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const isLeftHalf = e.clientX < centerX;

          setHoveredSide(isLeftHalf ? 'left' : 'right');
        }

        const handleDrop: DragEventHandler<HTMLLIElement> = (e) => {
          e.preventDefault()

          const prevIndex = movingItemIndex!
          const newIndex = index

          if (
            movingItemIndex! + 1 === hoveredItemIndex && hoveredSide === 'left' ||
            movingItemIndex! - 1 === hoveredItemIndex && hoveredSide === 'right'
          ) {
            return
          }

          const prevOrder = [...activePendantProductVaraintIds]
          const selectedPendantVariantId = prevOrder[prevIndex]

          const orderWithoutSelectedVariantId = [
            ...prevOrder.slice(0, prevIndex), 
            ...prevOrder.slice(prevIndex + 1, prevOrder.length)
          ]

          orderWithoutSelectedVariantId.slice(0, newIndex)
          prevOrder[prevIndex]

          const newOrder = [
            ...orderWithoutSelectedVariantId.slice(0, newIndex),
            selectedPendantVariantId,
            ...orderWithoutSelectedVariantId.slice(newIndex, orderWithoutSelectedVariantId.length)
          ]
      
          setActivePendantProductVaraintIds(newOrder)
          setActive(newIndex)
        }

        const handleDrugEnd: DragEventHandler<HTMLLIElement> = () => {
          setMovingItemIndex(null)
          setHoveredItemIndex(null)
        }
  
        return (
          <li 
            key={index} 
            className={classNames(
              "relative shrink-0 transition-all duration-100", 
              {
                'pr-[70px] xl:pr-[100px]': index === hoveredItemIndex,
                'opacity-50': index === movingItemIndex,
                'ios-shake': iosShakeOn
              }
            )}
            draggable={dragAndDropEnabled}
            onDragStart={handleDrugStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnd={handleDrugEnd}
          > 
            <button 
              type="button"
              className={classNames(
                "block w-[40px] h-[40px] xl:min-w-[48px] xl:h-[48px] bg-[#f8f7f5] border border-subdued", 
                active === index ? "border-solid" : "border-dashed",
                "transition-all duration-100",
                {
                  'translate-x-[70px] xl:translate-x-[100px]': (  
                    index === hoveredItemIndex && 
                    hoveredSide === 'left'
                  ),
                }
              )}
              onClick={handlePendantSelect}
            > 
              {pendantProductVariant && <img 
                className="w-full h-full pointer-events-none"
                src={pendantProductVariant.featuredImageUrl} 
                alt={pendantProductVariant.title} 
                width="40"
                height="40"
              />}
            </button>

            {(
              active === index && 
              activePendantProductVaraintIds.length > MIN_NUMBER_OF_PENDANTS &&
              movingItemIndex === null
            ) && (
              <button
                type="button"
                className="absolute -top-2 -right-2 p-[3px] bg-white rounded-full"
                style={{ outline: '1px solid black' }} // tw doesn't work here for some reason @TODO
                aria-label="Remove pendant"
                onClick={() => handlePendantRemove(index)} 
              >
                <svg
                  className="block w-3 h-3 shrink-0"
                  fill="none"
                  viewBox="0 0 5 5"
                >
                  <g clipPath="url(#a)">
                    <path
                      stroke="#1a1919"
                      strokeMiterlimit={10}
                      strokeWidth={0.5}
                      d="M4 1 1.043 4m2.915 0L1 1"
                    />
                  </g>
                  <defs>
                    <clipPath id="a">
                      <path fill="#fff" d="M0 0h5v5H0z" />
                    </clipPath>
                  </defs>
                </svg>
              </button>
            )} 
          </li>
        )
      })}

      {(
        activePendantProductVaraintIds.length < MAX_NUMBER_OF_PENDANTS &&
        movingItemIndex === null
      ) && (
        <li key="-1">
          <button
            type="button"
            className="flex justify-center items-center w-[40px] h-[40px] xl:min-w-[48px] xl:h-[48px] border border-dashed border-subdued"
            aria-label="Add one more pendant"
            onClick={handlePendantAdd}  
          > 
            <svg
              className="w-3 h-3"
              fill="none"
              viewBox="0 0 14 14"
            >
              <path
                stroke="currentColor"
                d="M7 .333v13.334M.332 7h13.333"
              />
            </svg>
          </button>
        </li>
      )}

      {(movingItemIndex === null) && Array
        .from({ length: MAX_NUMBER_OF_PENDANTS - activePendantProductVaraintIds.length - 1})
        .map((_, index) => {
          return (
            <li 
              key={activePendantProductVaraintIds.length + index} 
              className="flex justify-center items-center w-[40px] h-[40px] xl:min-w-[48px] xl:h-[48px] px-2 border border-dashed border-[#D7D7D7]" 
            />
          )
        })
      }
    </ul>
  )
}